import React, { FC }             from 'react';
import styled                    from 'styled-components';
import {
  IReport,
  ISalesPersonTableRow,
  IReportCU,
  IReportPayment,
}                                from '@typings/accountManagement';
import {
  EAppPage,
  EMonthNumber,
}                                from '@enums/common';
import { EAccountPage }          from '@enums/accountManagement';
import {
  EFeeType,
  EFeeUserType,
}                                from '@enums/invoiceManagement';
import { useFetchReports }       from '@modules/accountManagement/queries/accountQueries';
import { PRICING_LABELS }        from '@modules/invoiceManagement/utils/suggestions';
import { ReportDetailsByPerson } from '@modules/accountManagement/pages/ReportDetailsByPerson';
import { LinksWrapper }          from '@components/LinksWrapper/LinksWrapper';
import { LinkButton }            from '@components/LinkButton/LinkButton';

const Wrapper = styled.div`
  padding: 20px 30px 60px;

  @media (min-width: 1280px) {
    padding: 20px 100px 60px;
  }
`;

const getCuFeeValues = (
  creditUnions : IReportCU[],
  feeType      : EFeeType | EFeeUserType,
  isUserFee    = false,
): ISalesPersonTableRow['values'] => creditUnions.map((CU) => {
  const currentPayment = CU.payments.find((payment) => {
    const feeKey: keyof IReportPayment = isUserFee ? 'feeUserType' : 'feeType';

    return payment[feeKey] === feeType;
  });

  return currentPayment?.value ?? 0;
});

const getReportRows = ({ creditUnions, totalAmount }: IReport): ISalesPersonTableRow[] => [{
  label  : PRICING_LABELS.monthlyFee,
  values : getCuFeeValues(creditUnions, EFeeType.Monthly),
}, {
  label  : PRICING_LABELS.personalFee,
  values : getCuFeeValues(creditUnions, EFeeUserType.PersonalId, true),
}, {
  label  : PRICING_LABELS.dealerFee,
  values : getCuFeeValues(creditUnions, EFeeUserType.DealerId, true),
}, {
  label  : PRICING_LABELS.nadaMonthlyFee,
  values : getCuFeeValues(creditUnions, EFeeType.NadaMonthlyFee).some((num) => num !== 0)
    ? getCuFeeValues(creditUnions, EFeeType.NadaMonthlyFee)
    : [],
}, {
  label  : PRICING_LABELS.nadaBookOutFee,
  values : getCuFeeValues(creditUnions, EFeeType.NadaBookOutFee).some((num) => num !== 0)
    ? getCuFeeValues(creditUnions, EFeeType.NadaBookOutFee)
    : [],
}, {
  label  : PRICING_LABELS.expressRecoveryFee,
  values : getCuFeeValues(creditUnions, EFeeType.ExpressRecoveryMonthlyFee),
}, {
  label  : PRICING_LABELS.deficiencyTrackingFee,
  values : getCuFeeValues(creditUnions, EFeeType.DeficiencyTrackingFee),
}, {
  label  : PRICING_LABELS.vrmFee,
  values : getCuFeeValues(creditUnions, EFeeType.VrmFee),
}, {
  label  : PRICING_LABELS.srsFee,
  values : getCuFeeValues(creditUnions, EFeeType.SrsFee),
}, {
  label  : PRICING_LABELS.reindeerFee,
  values : getCuFeeValues(creditUnions, EFeeType.ReindeerFee),
}, {
  label   : 'Total Amount',
  values  : creditUnions.map((CU) => CU.totalAmount),
  isTotal : true,
}, {
  label   : 'Total',
  values  : [totalAmount],
  isTotal : true,
}];

interface IProps {
  year  : number;
  month : EMonthNumber;
  push  : (path: string) => void;
}

export const ReportsDetailsContainer: FC<IProps> = ({ year, month, push }) => {
  const { data: reports = [] as IReport[] } = useFetchReports(
    { month, year },
    { enabled: !!(month && year) },
  );

  return (
    <Wrapper>
      <LinksWrapper>
        <LinkButton
          label   = "Return to Reports List"
          onClick = {(): void => push(`${EAppPage.AccountManagement}/${EAccountPage.Reports}`)}
        />
      </LinksWrapper>

      {reports.map((report) => (
        <ReportDetailsByPerson
          key             = {report.salesPersonName}
          salesPersonName = {report.salesPersonName}
          creditUnions    = {report.creditUnions}
          reportRows      = {getReportRows(report)}
        />
      ))}
    </Wrapper>
  );
};
